/**
 * DENEB London Theme - Collection Page
 *
 * Extracted from sections/collection.liquid (Task 20a) so the bulk of the
 * collection page JS can be cached as a static asset instead of re-sent
 * inline on every collection page view.
 *
 * The <script type="application/json" data-collection-config> element rendered
 * by the section is both the section marker and the per-DOM init guard. A
 * main-content swap brings in a fresh marker, so the replacement collection
 * is initialized without relying on state left on the persistent <body>.
 *
 * Globals relied upon (defined elsewhere, intentionally not duplicated):
 *   - formatMoney()  -> assets/theme.js
 *   - getWishlist()  -> layout/theme.liquid (guarded with typeof check)
 *
 * Editor-safe init: initialises once on normal page load, and re-initialises
 * against fresh DOM on shopify:section:load. Document-level listeners are
 * registered only once (guarded by documentListenersBound).
 */
(function () {
  var documentListenersBound = false;

  function navigateCollection(url) {
    try {
      if (window.theme && typeof window.theme.avenithNavigate === 'function') {
        if (window.theme.avenithNavigate(url)) return;
      }
    } catch (error) {
      // Voice navigation is an enhancement; preserve the normal collection navigation.
    }
    window.location.assign(url);
  }

  function initCollection() {
    var configEl = document.querySelector('[data-collection-config]');
    if (!configEl) return;

    // Guard against double-init against this collection DOM. <body> survives
    // Avenith swaps, so it cannot safely own a page-scoped initialization bit.
    if (configEl.dataset.collectionJsInit === 'true') return;
    configEl.dataset.collectionJsInit = 'true';

    // Detect active variant finish from URL (re-read at call time so Load More
    // respects filter clicks that update the URL via history.pushState).
    // Product materials such as Sterling Silver and Stainless Steel are tags,
    // not Finish option values, and are filtered server-side by Shopify.
    function detectActiveFinish() {
      var currentUrl = window.location.href.toLowerCase();
      if (currentUrl.includes('white-gold') || currentUrl.includes('white_gold')) {
        return 'white';
      } else if (currentUrl.includes('rose-gold') || currentUrl.includes('rose_gold')) {
        return 'rose';
      } else if (currentUrl.includes('yellow-gold') || currentUrl.includes('yellow_gold')) {
        return 'yellow';
      }
      return null;
    }
    let activeFinish = detectActiveFinish();

    // ── Shared helpers ────────────────────────────────────────────────

    // Find the swatch matching a finish keyword inside a swatches container
    function findSwatchForFinish(swatchesContainer, finish) {
      if (finish === 'white') {
        return (
          swatchesContainer.querySelector('.product-card__swatch[data-finish-name*="white"]') ||
          swatchesContainer.querySelector('.product-card__swatch[data-finish-name*="silver"]')
        );
      }
      return swatchesContainer.querySelector('.product-card__swatch[data-finish-name*="' + finish + '"]');
    }

    // Selectors describing how a collection product card is laid out. Shared
    // with the swatch click handlers below and passed straight to the shared
    // window.theme card-update helpers.
    var collectionSwatchOptions = {
      cardSelector: '.product-card',
      primaryImageSelector: '.product-card__image--primary img',
      hoverImageSelector: '.product-card__image--hover img',
      hoverImageLinkSelector: '.product-card__image-link',
      linkSelectors: ['.product-card__image-link', '.product-card__title a'],
      priceSelector: '.product-card__price'
    };

    // Update a single product card's images and links from a swatch's data
    // attributes. Thin wrapper over the shared primitive so the finish-filter
    // helpers (applyFinishToAllCards / resetAllCards) can drive it directly,
    // not just on click.
    function updateCardFromSwatch(swatchesContainer, swatch) {
      const productCard = swatchesContainer.closest('.product-card');
      window.theme.updateProductCardFromSwatch(productCard, swatchesContainer, swatch, collectionSwatchOptions);
    }

    // Apply a finish to every product card — hide cards that don't carry that finish
    function applyFinishToAllCards(finish) {
      document.querySelectorAll('.product-card__swatches').forEach(function (swatchesContainer) {
        const productCard = swatchesContainer.closest('.product-card');
        const swatch = findSwatchForFinish(swatchesContainer, finish);
        if (swatch) {
          productCard.style.display = '';
          updateCardFromSwatch(swatchesContainer, swatch);
        } else {
          productCard.style.display = 'none';
        }
      });
      syncEmptyState();
    }

    // Reset all cards to visible and their first (default) swatch
    function resetAllCards() {
      document.querySelectorAll('.product-card__swatches').forEach(function (swatchesContainer) {
        const productCard = swatchesContainer.closest('.product-card');
        productCard.style.display = '';
        const firstSwatch = swatchesContainer.querySelector('.product-card__swatch');
        if (firstSwatch) updateCardFromSwatch(swatchesContainer, firstSwatch);
      });
      syncEmptyState();
    }

    // Show/hide the JS filter empty state based on visible card count
    function syncEmptyState() {
      const allCards = document.querySelectorAll('#CollectionProductGrid .product-card');
      const hiddenCount = Array.prototype.filter.call(allCards, function (c) {
        return c.style.display === 'none';
      }).length;
      const filterEmptyState = document.getElementById('CollectionFilterEmptyState');
      const productGrid = document.getElementById('CollectionProductGrid');
      const allHidden = allCards.length > 0 && hiddenCount === allCards.length;
      if (productGrid) productGrid.style.display = allHidden ? 'none' : '';
      if (filterEmptyState) filterEmptyState.style.display = allHidden ? '' : 'none';
    }

    // "Clear filter" button inside the JS empty state
    const filterClearBtn = document.getElementById('CollectionFilterClearBtn');
    if (filterClearBtn) {
      filterClearBtn.addEventListener('click', function () {
        document.querySelectorAll('.collection-intro__categories .collection-category').forEach(function (btn) {
          btn.classList.remove('is-active');
        });
        resetAllCards();
        if (baseCollectionUrl) window.history.pushState({}, '', baseCollectionUrl);
      });
    }

    // ── On page load: sync cards + filter buttons to URL filter ──────

    if (activeFinish) {
      applyFinishToAllCards(activeFinish);

      // Mark the matching hero filter button as active
      document.querySelectorAll('.collection-intro__categories .collection-category').forEach(function (btn) {
        const href = (btn.getAttribute('href') || '').toLowerCase();
        if (
          (activeFinish === 'white' && href.includes('white-gold')) ||
          (activeFinish === 'rose' && href.includes('rose-gold')) ||
          (activeFinish === 'yellow' && href.includes('yellow-gold'))
        ) {
          btn.classList.add('is-active');
        }
      });
    }

    // ── Per-card swatch click handler ─────────────────────────────────
    // Shared with the search page (see assets/theme.js). guardInitialized
    // flags each container so Load More re-init doesn't double-bind.

    window.theme.initProductCardSwatches(document, Object.assign({ guardInitialized: true }, collectionSwatchOptions));

    // ── Hero filter button click handler (instant client-side swap) ───

    const filterButtons = document.querySelectorAll('.collection-intro__categories .collection-category');
    const filterContainer = document.querySelector('.collection-intro__categories');
    const baseCollectionUrl = filterContainer ? filterContainer.dataset.collectionUrl : null;

    filterButtons.forEach(function (filterBtn) {
      filterBtn.addEventListener('click', function (e) {
        const href = this.getAttribute('href') || '';
        let targetFinish = null;
        if (href.includes('white-gold')) targetFinish = 'white';
        else if (href.includes('rose-gold')) targetFinish = 'rose';
        else if (href.includes('yellow-gold')) targetFinish = 'yellow';

        if (!targetFinish) return;
        e.preventDefault();

        // Toggle off: clicking the already-active filter resets all cards
        if (this.classList.contains('is-active')) {
          this.classList.remove('is-active');
          resetAllCards();
          if (baseCollectionUrl) window.history.pushState({}, '', baseCollectionUrl);
          return;
        }

        // Mark this button active, clear others
        filterButtons.forEach(function (btn) {
          btn.classList.remove('is-active');
        });
        this.classList.add('is-active');

        // Instantly swap every product card to the selected finish
        applyFinishToAllCards(targetFinish);

        // Update the URL (no page reload)
        window.history.pushState({}, '', href);
      });
    });

    // ── Load More ─────────────────────────────────────────────────────
    var loadMoreBtn = document.getElementById('LoadMoreButton');

    function initSwatchHandlers() {
      window.theme.initProductCardSwatches(document, Object.assign({ guardInitialized: true }, collectionSwatchOptions));
    }

    function initWishlistStates() {
      if (typeof getWishlist !== 'function') return;
      var wishlist = getWishlist();
      var wishlistIds = wishlist.map(function (item) {
        return String(item.id);
      });
      document.querySelectorAll('.product-card__wishlist').forEach(function (btn) {
        if (wishlistIds.indexOf(String(btn.dataset.productId)) !== -1) {
          btn.classList.add('is-wishlisted');
        }
      });
    }

    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', function (e) {
        e.preventDefault();
        if (loadMoreBtn.getAttribute('aria-disabled') === 'true') return;
        var nextUrl = this.getAttribute('data-next-url');
        if (!nextUrl) return;

        var textEl = this.querySelector('.collection-load-more__text');
        var spinnerEl = this.querySelector('.collection-load-more__spinner');
        textEl.style.display = 'none';
        spinnerEl.style.display = '';
        loadMoreBtn.setAttribute('aria-disabled', 'true');
        loadMoreBtn.style.pointerEvents = 'none';

        var separator = nextUrl.includes('?') ? '&' : '?';
        var fetchUrl = nextUrl + separator + 'section_id=collection';

        fetch(fetchUrl)
          .then(function (response) {
            return response.text();
          })
          .then(function (text) {
            var html = document.createElement('div');
            html.innerHTML = text;

            var newGrid = html.querySelector('#CollectionProductGrid');
            if (newGrid) {
              var newItems = newGrid.querySelectorAll('.product-card');
              var currentGrid = document.getElementById('CollectionProductGrid');

              newItems.forEach(function (item) {
                currentGrid.appendChild(item);
              });
            }

            var newLoadMoreBtn = html.querySelector('#LoadMoreButton');
            if (newLoadMoreBtn && newLoadMoreBtn.getAttribute('data-next-url')) {
              loadMoreBtn.setAttribute('data-next-url', newLoadMoreBtn.getAttribute('data-next-url'));
              textEl.style.display = '';
              spinnerEl.style.display = 'none';
              loadMoreBtn.removeAttribute('aria-disabled');
              loadMoreBtn.style.pointerEvents = '';
            } else {
              document.getElementById('CollectionLoadMore').style.display = 'none';
            }

            initSwatchHandlers();
            initWishlistStates();

            var currentFinish = detectActiveFinish();
            if (currentFinish) {
              applyFinishToAllCards(currentFinish);
            }
          })
          .catch(function (err) {
            console.error('Load More failed:', err);
            textEl.style.display = '';
            spinnerEl.style.display = 'none';
            loadMoreBtn.removeAttribute('aria-disabled');
            loadMoreBtn.style.pointerEvents = '';
          });
      });
    }

    // Sort functionality
    const sortSelect = document.getElementById('SortBy');
    if (sortSelect) {
      sortSelect.addEventListener('change', function () {
        if (this.value === 'random') {
          shuffleCollectionProducts();
          return;
        }
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', this.value);
        navigateCollection(url.toString());
      });
    }

    // Shuffle collection products client-side
    function shuffleCollectionProducts() {
      const grid = document.querySelector('.collection-products');
      if (!grid) return;

      const cards = Array.from(grid.querySelectorAll('.product-card'));
      if (cards.length <= 1) return;

      // Fisher-Yates shuffle
      for (let i = cards.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [cards[i], cards[j]] = [cards[j], cards[i]];
      }

      // Re-append in new order
      cards.forEach(function (card) {
        grid.appendChild(card);
      });

      // Scroll to top of products
      const toolbar = document.querySelector('.collection-toolbar');
      if (toolbar) {
        toolbar.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    // Newest-first default ordering comes from each collection's admin
    // "Sort products" setting — no client-side ?sort_by redirect here
    // (/collections/*sort_by* is robots-disallowed; redirecting every
    // parameterless visit made collection pages non-indexable and
    // double-loaded them). Explicit dropdown picks still navigate with
    // sort_by via the change handler above.

    // ── Filter System ─────────────────────────────────────────────────────
    const filterBtn = document.querySelector('[data-mobile-filter-toggle]');
    const filterSidebar = document.querySelector('.collection-intro__categories');
    const filterModal = document.getElementById('filter-modal');
    let mobileBackdrop = null;

    // Check if mobile view
    function isMobileView() {
      return window.innerWidth <= 768;
    }

    // ── Mobile Filter Drawer (Finish only) ──────────────────────────────
    function closeMobileFilterDrawer() {
      if (filterSidebar) {
        filterSidebar.classList.remove('is-open');
      }
      if (filterBtn) {
        filterBtn.classList.remove('is-active');
        filterBtn.setAttribute('aria-expanded', 'false');
      }
      if (mobileBackdrop) {
        mobileBackdrop.remove();
        mobileBackdrop = null;
      }
      document.body.style.overflow = '';
    }

    function openMobileFilterDrawer() {
      if (filterSidebar) {
        filterSidebar.classList.add('is-open');
      }
      if (filterBtn) {
        filterBtn.classList.add('is-active');
        filterBtn.setAttribute('aria-expanded', 'true');
      }
      // Create backdrop
      mobileBackdrop = document.createElement('div');
      mobileBackdrop.className = 'collection-filter-backdrop';
      mobileBackdrop.style.cssText =
        'position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:999;opacity:0;transition:opacity 0.3s ease;';
      document.body.appendChild(mobileBackdrop);
      // Trigger reflow
      mobileBackdrop.offsetHeight;
      mobileBackdrop.style.opacity = '1';
      mobileBackdrop.addEventListener('click', closeMobileFilterDrawer);
      document.body.style.overflow = 'hidden';
    }

    // ── Desktop Filter Modal ────────────────────────────────────────────
    function openFilterModal() {
      if (!filterModal) return;
      filterModal.classList.add('is-open');
      filterModal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      // Focus the close button
      const closeBtn = filterModal.querySelector('.filter-modal__close');
      if (closeBtn) {
        setTimeout(() => closeBtn.focus(), 100);
      }

      // Sync current filters to modal checkboxes
      syncFiltersToModal();
    }

    function closeFilterModal() {
      if (!filterModal) return;
      filterModal.classList.remove('is-open');
      filterModal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    // Sync URL parameters to modal checkboxes
    function syncFiltersToModal() {
      if (!filterModal) return;
      const url = window.location.href.toLowerCase();
      const params = new URLSearchParams(window.location.search);

      // Reset all checkboxes
      filterModal.querySelectorAll('.filter-checkbox__input').forEach(cb => cb.checked = false);

      // Check finish filters from URL path or query param
      const finishParam = (params.get('finish') || params.get('constraint') || '').toLowerCase();
      if (finishParam) {
        const finish = filterModal.querySelector(`[name="finish"][value="${CSS.escape(finishParam.toLowerCase())}"]`);
        if (finish) finish.checked = true;
      }
      if (url.includes('/white-gold') || url.includes('/white_gold')) {
        const finish = filterModal.querySelector('[name="finish"][value="white-gold"]');
        if (finish) finish.checked = true;
      }
      if (url.includes('/rose-gold') || url.includes('/rose_gold')) {
        const finish = filterModal.querySelector('[name="finish"][value="rose-gold"]');
        if (finish) finish.checked = true;
      }
      if (url.includes('/yellow-gold') || url.includes('/yellow_gold')) {
        const finish = filterModal.querySelector('[name="finish"][value="yellow-gold"]');
        if (finish) finish.checked = true;
      }
      if (url.includes('/sterling-silver')) {
        const finish = filterModal.querySelector('[name="finish"][value="sterling-silver"]');
        if (finish) finish.checked = true;
      }
      if (url.includes('/stainless-steel')) {
        const finish = filterModal.querySelector('[name="finish"][value="stainless-steel"]');
        if (finish) finish.checked = true;
      }

      // Check stone filters from URL path or query param
      const stoneNames = ['garnet', 'amethyst', 'aquamarine', 'diamond', 'emerald', 'pearl', 'alexandrite', 'ruby', 'peridot', 'sapphire', 'tourmaline', 'blue-topaz', 'citrine', 'tanzanite'];
      const stoneParam = params.get('stone');
      stoneNames.forEach(stone => {
        if (stoneParam && stoneParam.toLowerCase() === stone) {
          const cb = filterModal.querySelector(`[name="stone"][value="${CSS.escape(stone)}"]`);
          if (cb) cb.checked = true;
        } else if (url.includes('/' + stone)) {
          const cb = filterModal.querySelector(`[name="stone"][value="${CSS.escape(stone)}"]`);
          if (cb) cb.checked = true;
        }
      });

      // Check price filters from Shopify native params
      const priceLte = params.get('filter.v.price.lte');
      const priceGte = params.get('filter.v.price.gte');
      if (priceLte && !priceGte) {
        // Under X price
        const cb = filterModal.querySelector('[name="price"][value="under-40"]');
        if (cb) cb.checked = true;
      } else if (priceGte && !priceLte) {
        // Over X price
        const cb = filterModal.querySelector('[name="price"][value="over-100"]');
        if (cb) cb.checked = true;
      } else if (priceGte && priceLte) {
        // Range
        const cb = filterModal.querySelector('[name="price"][value="40-100"]');
        if (cb) cb.checked = true;
      }

      // Check product type filters from constraint param
      const typeParam = params.get('type');
      if (typeParam) {
        const types = typeParam.split(',');
        types.forEach(type => {
          const cb = filterModal.querySelector(`[name="product-type"][value="${CSS.escape(type.trim())}"]`);
          if (cb) cb.checked = true;
        });
      }
    }

    // Get the collection base URL (e.g., /collections/necklaces)
    function getCollectionBaseUrl() {
      const path = window.location.pathname;

      // For collection hubs, preserve the collection line (e.g., /collections/charms/halo-of-light)
      if (path.match(/^\/collections\/charms\/[^\/]+/)) {
        // Extract just the collection line, remove any finish/stone filter from path
        const match = path.match(/^(\/collections\/charms\/[^\/]+)/);
        if (match) {
          return match[1];
        }
      }

      // For regular collections, extract just the collection path (remove finish/stone segments)
      const match = path.match(/^\/collections\/([^\/]+)/);
      if (match) {
        return '/collections/' + match[1];
      }
      return path;
    }

    // Build URL from selected filters
    function buildFilterUrl() {
      const baseUrl = getCollectionBaseUrl();
      const params = new URLSearchParams();
      let filterPath = baseUrl;

      // Get selected finishes
      const selectedFinishes = [];
      filterModal.querySelectorAll('[name="finish"]:checked').forEach(cb => {
        selectedFinishes.push(cb.value);
      });

      // Get selected stones
      const selectedStones = [];
      filterModal.querySelectorAll('[name="stone"]:checked').forEach(cb => {
        selectedStones.push(cb.value);
      });

      // Get selected price
      const selectedPrice = filterModal.querySelector('[name="price"]:checked');

      // Get selected product types
      const selectedTypes = [];
      filterModal.querySelectorAll('[name="product-type"]:checked').forEach(cb => {
        selectedTypes.push(cb.value);
      });

      // Material tags use Shopify's native collection/tag path so the server
      // returns only matching products. Variant finishes remain query-driven
      // and are applied to cards client-side.
      if (selectedFinishes.length > 0) {
        const selectedFinish = selectedFinishes[0];
        if (selectedFinish === 'sterling-silver' || selectedFinish === 'stainless-steel') {
          filterPath += '/' + selectedFinish;
        } else {
          params.set('finish', selectedFinish);
        }
      }
      if (selectedStones.length > 0) {
        params.set('stone', selectedStones[0]);
      }
      if (selectedTypes.length > 0) {
        params.set('type', selectedTypes.join(','));
      }

      // Add price filter using Shopify native params (same as header menu)
      if (selectedPrice) {
        const priceValue = selectedPrice.value;
        if (priceValue === 'under-40') {
          params.set('filter.v.price.lte', '40');
        } else if (priceValue === '40-100') {
          params.set('filter.v.price.gte', '40');
          params.set('filter.v.price.lte', '100');
        } else if (priceValue === 'over-100') {
          params.set('filter.v.price.gte', '100');
        }
      }

      // Preserve sort_by if exists
      const currentParams = new URLSearchParams(window.location.search);
      if (currentParams.has('sort_by')) {
        params.set('sort_by', currentParams.get('sort_by'));
      }

      const queryString = params.toString();
      return queryString ? filterPath + '?' + queryString : filterPath;
    }

    // Clear all filters
    function clearAllFilters() {
      filterModal.querySelectorAll('.filter-checkbox__input').forEach(cb => cb.checked = false);
      const baseUrl = getCollectionBaseUrl();
      const currentParams = new URLSearchParams(window.location.search);
      const cleanParams = new URLSearchParams();
      if (currentParams.has('sort_by')) {
        cleanParams.set('sort_by', currentParams.get('sort_by'));
      }
      const queryString = cleanParams.toString();
      navigateCollection(queryString ? baseUrl + '?' + queryString : baseUrl);
    }

    // Apply filters
    function applyFilters() {
      const url = buildFilterUrl();
      navigateCollection(url);
    }

    // ── Client-Side Filtering for legacy material, stone & type params ─
    // Since Shopify Liquid can't read query parameters, we filter
    // product cards on the client side for query-string filters. New material
    // selections use native tag paths; support finish=/constraint= here so old
    // links still show the correct inventory instead of the full collection.
    function applyClientSideFilters() {
      const params = new URLSearchParams(window.location.search);
      const finishParam = (params.get('finish') || params.get('constraint') || '').toLowerCase();
      const materialParam = finishParam === 'sterling-silver' || finishParam === 'stainless-steel'
        ? finishParam.replace('-', ' ')
        : null;
      const stoneParam = params.get('stone');
      const typeParam = params.get('type');

      if (!materialParam && !stoneParam && !typeParam) return;

      const productCards = document.querySelectorAll('.product-card[data-product-handle]');
      let visibleCount = 0;

      productCards.forEach(function (card) {
        const tagsAttr = card.getAttribute('data-product-tags') || '';
        const productType = card.getAttribute('data-product-type') || '';
        const tags = tagsAttr.split(',').map(t => t.trim());
        let showCard = true;

        if (materialParam && !tags.includes(materialParam)) {
          showCard = false;
        }

        // Filter by stone type (check tags)
        if (stoneParam) {
          const hasStone = tags.some(function (tag) {
            return tag === stoneParam.toLowerCase() ||
                   tag === stoneParam.toLowerCase().replace('-', ' ');
          });
          if (!hasStone) showCard = false;
        }

        // Filter by product type (check type or tags).
        // Plural-tolerant so "ring" matches a "Rings" product type, etc.
        if (typeParam) {
          const normalizeType = function (s) {
            return String(s).toLowerCase().trim().replace(/s$/, '');
          };
          const selectedTypes = typeParam.toLowerCase().split(',').map(function (t) {
            return normalizeType(t);
          });
          const cardType = normalizeType(productType);
          const hasType = selectedTypes.some(function (tv) {
            if (tv === '') return false;
            if (cardType === tv) return true;
            return tags.some(function (tag) {
              return normalizeType(tag) === tv;
            });
          });
          if (!hasType) showCard = false;
        }

        if (showCard) {
          card.style.display = '';
          visibleCount++;
        } else {
          card.style.display = 'none';
        }
      });

      // Update product count display
      const countEl = document.querySelector('.collection-toolbar__count');
      if (countEl) {
        const word = visibleCount === 1 ? 'product' : 'products';
        countEl.textContent = visibleCount + ' ' + word;
      }

      // Show empty state if no products visible
      const emptyState = document.getElementById('CollectionEmptyState');
      const grid = document.getElementById('CollectionProductGrid');
      if (visibleCount === 0 && emptyState && grid) {
        grid.style.display = 'none';
        emptyState.style.display = 'block';
      }
    }

    // Run client-side filters after DOM is ready
    applyClientSideFilters();

    // Filter button click handler
    if (filterBtn) {
      filterBtn.addEventListener('click', function () {
        openFilterModal();
      });
    }

    // Filter modal event listeners
    if (filterModal) {
      const closeBtn = filterModal.querySelector('.filter-modal__close');
      const cancelBtn = filterModal.querySelector('[data-cancel-filters]');
      const applyBtn = filterModal.querySelector('[data-apply-filters]');
      const clearBtn = filterModal.querySelector('[data-clear-all-filters]');
      const backdrop = filterModal.querySelector('.filter-modal__backdrop');

      if (closeBtn) closeBtn.addEventListener('click', closeFilterModal);
      if (cancelBtn) cancelBtn.addEventListener('click', closeFilterModal);
      if (applyBtn) applyBtn.addEventListener('click', applyFilters);
      if (clearBtn) clearBtn.addEventListener('click', clearAllFilters);
      if (backdrop) backdrop.addEventListener('click', closeFilterModal);
    }

    // Document-level keyboard listeners are bound only once so editor
    // section reloads don't stack duplicate handlers. They read the
    // current modal/sidebar from the live DOM at event time.
    if (!documentListenersBound) {
      documentListenersBound = true;

      // Close filter modal / mobile drawer on escape key
      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        const liveModal = document.getElementById('filter-modal');
        const liveSidebar = document.querySelector('.collection-intro__categories');
        if (liveModal && liveModal.classList.contains('is-open')) {
          liveModal.classList.remove('is-open');
          liveModal.setAttribute('aria-hidden', 'true');
          document.body.style.overflow = '';
        }
        if (liveSidebar && liveSidebar.classList.contains('is-open')) {
          // Use live-DOM queries so this handler works after shopify:section:load
          // re-renders the collection section (stale closure vars would be invalid).
          liveSidebar.classList.remove('is-open');
          var liveFilterBtn = document.querySelector('[data-mobile-filter-toggle]');
          if (liveFilterBtn) {
            liveFilterBtn.classList.remove('is-active');
            liveFilterBtn.setAttribute('aria-expanded', 'false');
          }
          var liveBackdrop = document.querySelector('.collection-filter-backdrop');
          if (liveBackdrop) {
            liveBackdrop.remove();
          }
          document.body.style.overflow = '';
        }
      });
    }

    // Product card video: play on hover (desktop) or tap (mobile)
    document.querySelectorAll('.product-card__video').forEach(function (video) {
      var card = video.closest('.product-card');
      if (!card) return;

      // Lazy-load video src via IntersectionObserver
      var videoObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && video.dataset.src) {
            video.src = video.dataset.src;
            video.removeAttribute('data-src');
            video.load();
            videoObserver.unobserve(video);
          }
        });
      }, { rootMargin: '200px' });
      videoObserver.observe(video);

      if (window.innerWidth > 768) {
        // Desktop: play on hover
        card.addEventListener('mouseenter', function () {
          video.play().then(function () {
            video.classList.add('is-playing');
          }).catch(function () {});
        });
        card.addEventListener('mouseleave', function () {
          video.classList.remove('is-playing');
          video.pause();
          video.currentTime = 0;
        });
      } else {
        // Mobile: play on first tap, navigate on second
        var tapped = false;
        card.addEventListener('click', function (e) {
          if (!tapped && video.src) {
            e.preventDefault();
            tapped = true;
            video.play().then(function () {
              video.classList.add('is-playing');
            }).catch(function () {});
            setTimeout(function () { tapped = false; video.classList.remove('is-playing'); video.pause(); }, 3000);
          }
        });
      }
    });
  }

  // Run on DOMContentLoaded or immediately if already loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCollection);
  } else {
    initCollection();
  }

  // Re-init against fresh DOM when the section is reloaded in the theme editor
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target.querySelector('[data-collection-config]')) {
      initCollection();
    }
  });
})();
